import React, { useState, useEffect, useRef } from 'react';
import './FindMeModal.css';

const FindMeModal = ({ onClose, initialPerson }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedPerson, setSelectedPerson] = useState(initialPerson || null);
    const [loading, setLoading] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const panStart = useRef({ x: 0, y: 0 });

    const handleWheel = (e) => {
        e.preventDefault();
        const scaleAmount = -e.deltaY * 0.001;
        setZoomLevel(prev => Math.min(Math.max(prev + scaleAmount, 0.5), 4));
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        dragStart.current = { x: e.clientX, y: e.clientY };
        panStart.current = { ...pan };
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPan({
            x: panStart.current.x + dx,
            y: panStart.current.y + dy
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.5, 4));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.5, 0.5));

    // Reset pan when person changes
    useEffect(() => {
        setPan({ x: 0, y: 0 });
        setZoomLevel(1);
    }, [initialPerson, selectedPerson]);

    // Debug: Coordinate Picker
    const [debugCoords, setDebugCoords] = useState(null);

    const handleMapClick = (e) => {
        // Only trigger if not dragging
        // Check distance to ensure it's a click, not a drag
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 5) return;

        const svg = e.target.closest('svg');
        if (!svg) return;

        const point = svg.createSVGPoint();
        point.x = e.clientX;
        point.y = e.clientY;

        // Transform to SVG coordinates
        const svgPoint = point.matrixTransform(svg.getScreenCTM().inverse());

        // Snap to integer
        const x = Math.round(svgPoint.x);
        const y = Math.round(svgPoint.y);

        setDebugCoords({ x, y });
        console.log(`Clicked SVG Coords: X=${x}, Y=${y}`);
    };

    return (
        <div className="find-me-overlay" onClick={onClose}>
            <div className="find-me-modal" onClick={(e) => e.stopPropagation()}>
                <div className="find-me-header">
                    <h2>🔍 Find Someone</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="find-me-content">
                    {/* ... (search and details preserved) ... */}

                    {selectedPerson && (
                        <div className="person-detail">
                            {!initialPerson && (
                                <button
                                    className="back-btn"
                                    onClick={() => setSelectedPerson(null)}
                                >
                                    ← Back to results
                                </button>
                            )}

                            {selectedPerson.has_booking ? (
                                <>
                                    <div className="person-info">
                                        <h3>{selectedPerson.name}</h3>
                                        <p className="desk-location">
                                            📍 Desk <strong>{selectedPerson.desk_id}</strong> in {selectedPerson.zone} zone
                                        </p>
                                    </div>

                                    <div
                                        className="office-map-container"
                                        style={{
                                            overflow: 'hidden',
                                            position: 'relative',
                                            cursor: isDragging ? 'grabbing' : 'grab',
                                            touchAction: 'none',
                                            background: '#f9f9f9'
                                        }}
                                        onClick={handleMapClick}
                                        onWheel={handleWheel}
                                        onMouseDown={handleMouseDown}
                                        onMouseMove={handleMouseMove}
                                        onMouseUp={handleMouseUp}
                                        onMouseLeave={handleMouseUp}
                                    >
                                        <div className="map-controls" style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex', gap: '5px' }}>
                                            <button onClick={handleZoomOut} style={{ padding: '5px 10px', cursor: 'pointer', background: 'white', border: '1px solid #ccc', borderRadius: '4px' }}>-</button>
                                            <span style={{ background: 'white', padding: '5px', borderRadius: '4px', fontSize: '12px', minWidth: '40px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Math.round(zoomLevel * 100)}%</span>
                                            <button onClick={handleZoomIn} style={{ padding: '5px 10px', cursor: 'pointer', background: 'white', border: '1px solid #ccc', borderRadius: '4px' }}>+</button>
                                        </div>

                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomLevel})`,
                                            transformOrigin: 'center center',
                                            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                                        }}>
                                            <svg
                                                viewBox={
                                                    selectedPerson.zone.toLowerCase().includes('east') ? "0 0 810 937.5" :
                                                        selectedPerson.zone.toLowerCase().includes('west') ? "0 0 810 915" :
                                                            "0 0 1440 810"
                                                }
                                                className="office-layout-svg"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'block'
                                                }}
                                            >
                                                <image
                                                    href={
                                                        selectedPerson.zone.toLowerCase().includes('east') ? "/scripts/EAST.svg?v=2" :
                                                            selectedPerson.zone.toLowerCase().includes('west') ? "/scripts/WEST.svg?v=2" :
                                                                `/scripts/${selectedPerson.zone}.svg?v=2`
                                                    }
                                                    width={
                                                        selectedPerson.zone.toLowerCase().includes('east') ? "810" :
                                                            selectedPerson.zone.toLowerCase().includes('west') ? "810" :
                                                                "1440"
                                                    }
                                                    height={
                                                        selectedPerson.zone.toLowerCase().includes('east') ? "937.5" :
                                                            selectedPerson.zone.toLowerCase().includes('west') ? "915" :
                                                                "810"
                                                    }
                                                    preserveAspectRatio="xMidYMid meet"
                                                />

                                                {/* Anchor line from avatar to desk */}
                                                <line
                                                    x1={selectedPerson.position.x + selectedPerson.position.width / 2}
                                                    y1={selectedPerson.position.y - 60}
                                                    x2={selectedPerson.position.x + selectedPerson.position.width / 2}
                                                    y2={selectedPerson.position.y - 3}
                                                    className="avatar-anchor-line"
                                                    stroke="#FF6B6B"
                                                    strokeWidth="3"
                                                    strokeDasharray="6,4"
                                                    strokeLinecap="round"
                                                />

                                                {/* Pulsing desk marker */}
                                                <rect
                                                    x={selectedPerson.position.x - 3}
                                                    y={selectedPerson.position.y - 3}
                                                    width={selectedPerson.position.width + 6}
                                                    height={selectedPerson.position.height + 6}
                                                    className="desk-blink-marker"
                                                    rx="3"
                                                />

                                                {/* Avatar circle at the top */}
                                                <g transform={`translate(${selectedPerson.position.x + selectedPerson.position.width / 2}, ${selectedPerson.position.y - 60})`}>
                                                    {/* Glowing avatar circle */}
                                                    <circle
                                                        cx="0"
                                                        cy="0"
                                                        r="28"
                                                        className="find-me-avatar-glow"
                                                        fill="url(#avatarGradient)"
                                                        stroke="#FFD700"
                                                        strokeWidth="3"
                                                    />

                                                    {/* Avatar image or initial */}
                                                    {selectedPerson.profile_picture && selectedPerson.show_avatar ? (
                                                        <>
                                                            <defs>
                                                                <clipPath id="avatarClip">
                                                                    <circle cx="0" cy="0" r="25" />
                                                                </clipPath>
                                                            </defs>
                                                            <image
                                                                href={selectedPerson.profile_picture}
                                                                x="-25"
                                                                y="-25"
                                                                width="50"
                                                                height="50"
                                                                clipPath="url(#avatarClip)"
                                                                preserveAspectRatio="xMidYMid slice"
                                                            />
                                                        </>
                                                    ) : (
                                                        <text
                                                            x="0"
                                                            y="0"
                                                            textAnchor="middle"
                                                            dominantBaseline="central"
                                                            fontSize="20"
                                                            fontWeight="bold"
                                                            fill="white"
                                                        >
                                                            {selectedPerson.name.charAt(0).toUpperCase()}
                                                        </text>
                                                    )}

                                                    {/* Name label below avatar */}
                                                    <text
                                                        x="0"
                                                        y="45"
                                                        textAnchor="middle"
                                                        fontSize="12"
                                                        fontWeight="600"
                                                        fill="#333"
                                                        className="find-me-avatar-name"
                                                    >
                                                        {selectedPerson.name}
                                                    </text>
                                                </g>

                                                {/* Gradient definition for avatar */}
                                                <defs>
                                                    <linearGradient id="avatarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                        <stop offset="0%" stopColor="#667eea" />
                                                        <stop offset="100%" stopColor="#764ba2" />
                                                    </linearGradient>
                                                </defs>
                                            </svg>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="no-booking-detail">
                                    <h3>{selectedPerson.name}</h3>
                                    <p className="no-booking-message">
                                        No desk booking for today
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {!loading && searchQuery.trim().length >= 2 && searchResults.length === 0 && !selectedPerson && (
                        <div className="no-results">
                            No results found for "{searchQuery}"
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FindMeModal;
